// Lookups for the Applications policy adapter (../apps.jsx): the MFA API apps,
// the directories and their users, cached for a short while so the list,
// detail and editor pages (and the adapter's list()) share one read.
//
// The directory calls (utils/apiservice dispatch wrappers) share one
// AbortController per endpoint: a second call aborts the first, which then
// never settles. So they are made once here and shared, and each read gives up
// after READ_TIMEOUT_MS (then the screens act as if the directory is unknown).
import { directoryLabel, listDirectories, listDirectoryUsers, normalizePrincipal } from "../../../../service/appPolicyService";
import { listApps } from "../../../../service/idpService";

const FRESH_MS = 30000;
const READ_TIMEOUT_MS = 15000;
// Directory users read to search the picker and to check that a policy's user
// still exists. When the directory has more, unmatched users aren't flagged.
export const USERS_LOADED = 500;

const withTimeout = (promise) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), READ_TIMEOUT_MS))]);

// One shared promise per loader, reused while in flight and for FRESH_MS after.
function cached(load) {
  let entry = null;
  const get = () => {
    const now = Date.now();
    if (entry && (!entry.done || now - entry.at < FRESH_MS)) return entry.promise;
    const mine = { done: false, at: now };
    mine.promise = load().then(
      (v) => {
        mine.done = true;
        mine.at = Date.now();
        return v;
      },
      (e) => {
        if (entry === mine) entry = null;
        throw e;
      },
    );
    entry = mine;
    return mine.promise;
  };
  get.reset = () => {
    entry = null;
  };
  return get;
}

/** Every app the tenant has (MFA API and others): [{ id, name, protocol }], or null when the read timed out. */
export const loadApps = cached(() => withTimeout(listApps()).then((res) => (res ? res.apps || [] : null)));

/**
 * The directories and up to USERS_LOADED of their users:
 * { dirs, users, complete, known } where known is the set of normalised
 * "<directoryId>:<account>" and "<account>", or null when the directory
 * couldn't be read in full. Never rejects.
 */
export const loadDirectoryInfo = cached(() =>
  Promise.all([
    withTimeout(listDirectories()).catch(() => null),
    withTimeout(listDirectoryUsers({ pageSize: USERS_LOADED })).catch(() => null),
  ]).then(([dirs, res]) => {
    const users = res?.users || [];
    const complete = !!res && (Number(res.total) || 0) <= users.length;
    let known = null;
    if (complete) {
      known = new Set();
      users.forEach((u) => {
        const account = normalizePrincipal(u.account);
        known.add(`${u.directoryId}:${account}`);
        known.add(account);
      });
    }
    return { dirs: dirs || [], users, complete, known };
  }),
);

/** "<directoryId>:<account>", as the backend compares them. */
export const userKey = (directoryId, account) => `${Number(directoryId) || 0}:${normalizePrincipal(account)}`;

/** A policy's user no longer in the directory (false when we can't tell). */
export function userMissing(known, user, directoryId) {
  if (!known || !user) return false;
  const account = normalizePrincipal(user);
  if (!account) return false;
  return Number(directoryId) ? !known.has(`${Number(directoryId)}:${account}`) : !known.has(account);
}

/** The app's name, "<id> (deleted app)", or the id while apps are unknown. */
export function appText(apps, appId) {
  if (!appId) return "No application";
  if (!apps) return appId;
  const app = apps.find((a) => a.id === appId);
  return app ? app.name || appId : `${appId} (deleted app)`;
}

export const directoryText = (dirs, directoryId) => directoryLabel(dirs, directoryId);
